import axios from 'axios';

// Determine API base URL based on current environment
// On production (taptile.app), use same origin to avoid CORS issues
// On preview/development, use the environment variable
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    
    // Explicitly check for taptile production domain
    const isTaptileProduction = hostname === 'taptile.app' || 
                                 hostname.endsWith('.taptile.app');
    
    if (isTaptileProduction) {
      console.log('API: Production detected, using origin:', window.location.origin);
      return window.location.origin;
    }
  }
  
  // For all other environments (preview, localhost, etc.), use env URL
  console.log('API: Using env URL:', envUrl);
  return envUrl || '';
};

const BACKEND_URL = getApiBaseUrl();
const API_URL = BACKEND_URL + '/api';

// Single source of truth for the auth token.
// sessionStorage first: used by impersonation tabs and by the NexusCMS embed flow
// (/embed-auth), where no localStorage token exists at all.
export const getToken = () =>
  sessionStorage.getItem('token') || localStorage.getItem('token');

export const getCurrentUserId = () => {
  const token = getToken();
  if (!token) return null;
  try {
    return JSON.parse(atob(token.split('.')[1])).user_id;
  } catch {
    return null;
  }
};

// Create axios instance with timeout
const apiClient = axios.create({
  timeout: 30000, // 30 second timeout
});

// Add response interceptor for better error messages
apiClient.interceptors.response.use(
  response => response,
  error => {
    if (error.code === 'ECONNABORTED') {
      error.message = 'Request timed out. Please try again.';
    } else if (error.message === 'Network Error') {
      error.message = 'Unable to connect to server. Please check your connection and try again.';
    }
    return Promise.reject(error);
  }
);

const getAuthHeader = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Helper to get full image URL
export const getImageUrl = (imageUrl) => {
  if (!imageUrl) return '';
  
  // If it's already a full URL, return as-is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  
  // If it's a relative path (uploaded image), prepend backend URL
  // This works in both development (different ports) and production (same domain via ingress)
  if (imageUrl.startsWith('/uploads/') || imageUrl.startsWith('/api/uploads/')) {
    const fullUrl = BACKEND_URL + imageUrl;
    return fullUrl;
  }
  
  // Otherwise return as-is
  return imageUrl;
};

export const api = {
  // Customers
  getCustomers: async () => {
    const response = await apiClient.get(`${API_URL}/customers`, {
      headers: getAuthHeader()
    });
    return response.data;
  },
  
  getCustomer: async (id) => {
    const response = await apiClient.get(`${API_URL}/customers/${id}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },
  
  createCustomer: async (data) => {
    const response = await apiClient.post(`${API_URL}/customers`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },
  
  updateCustomer: async (id, data) => {
    const response = await apiClient.patch(`${API_URL}/customers/${id}`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // API Key management (for NexusCMS / external integrations)
  detectNexusRegion: async (payload) => {
    const response = await apiClient.post(`${API_URL}/nexus/detect-region`, payload, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getApiKeyStatus: async () => {
    const response = await apiClient.get(`${API_URL}/settings/api-key/status`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  generateApiKey: async () => {
    const response = await apiClient.post(`${API_URL}/settings/api-key/generate`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  revokeApiKey: async () => {
    const response = await apiClient.delete(`${API_URL}/settings/api-key`, {
      headers: getAuthHeader()
    });
    return response.data;
  },
  
  deleteCustomer: async (id) => {
    const response = await apiClient.delete(`${API_URL}/customers/${id}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Tiles
  getTiles: async () => {
    const response = await apiClient.get(`${API_URL}/tiles`, {
      headers: getAuthHeader()
    });
    return response.data;
  },
  
  createTile: async (data) => {
    const response = await apiClient.post(`${API_URL}/tiles`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },
  
  updateTile: async (id, data) => {
    const response = await apiClient.patch(`${API_URL}/tiles/${id}`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },
  
  deleteTile: async (id) => {
    const response = await apiClient.delete(`${API_URL}/tiles/${id}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Groups
  getGroups: async () => {
    const response = await apiClient.get(`${API_URL}/groups`, {
      headers: getAuthHeader()
    });
    return response.data;
  },
  
  createGroup: async (data) => {
    const response = await apiClient.post(`${API_URL}/groups`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },
  
  updateGroup: async (id, data) => {
    const response = await apiClient.patch(`${API_URL}/groups/${id}`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },
  
  deleteGroup: async (id) => {
    const response = await apiClient.delete(`${API_URL}/groups/${id}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Analytics
  trackAnalyticsEvent: async (data) => {
    const response = await apiClient.post(`${API_URL}/analytics/track`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getAnalyticsDashboard: async (params = {}) => {
    const response = await apiClient.get(`${API_URL}/analytics/dashboard`, {
      params,
      headers: getAuthHeader()
    });
    return response.data;
  },

  getSuperAdminAnalytics: async (params = {}) => {
    const response = await apiClient.get(`${API_URL}/analytics/super-admin`, {
      params,
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Notifications
  getNotifications: async () => {
    const response = await apiClient.get(`${API_URL}/notifications`, {
      headers: getAuthHeader()
    });
    return response.data;
  },
  
  createNotification: async (data) => {
    const response = await apiClient.post(`${API_URL}/notifications`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },
  
  markNotificationRead: async (id) => {
    const response = await apiClient.patch(`${API_URL}/notifications/${id}/read`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  deleteNotification: async (id) => {
    const response = await apiClient.delete(`${API_URL}/notifications/${id}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  deleteAllNotifications: async () => {
    const response = await apiClient.delete(`${API_URL}/notifications/all`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  stopRecurringNotification: async (id) => {
    const response = await apiClient.put(`${API_URL}/notifications/${id}/stop-recurring`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Users
  getUsers: async () => {
    const response = await apiClient.get(`${API_URL}/users`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  createUser: async (data) => {
    const response = await apiClient.post(`${API_URL}/auth/signup`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },
  
  updateUser: async (id, data) => {
    const response = await apiClient.patch(`${API_URL}/users/${id}`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },
  
  deleteUser: async (id) => {
    const response = await apiClient.delete(`${API_URL}/users/${id}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  uploadUsersCSV: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${API_URL}/users/upload-csv`, formData, {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  uploadGroupsCSV: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${API_URL}/groups/upload-csv`, formData, {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  suspendUser: async (userId) => {
    const response = await apiClient.patch(`${API_URL}/users/${userId}/suspend`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  activateUser: async (userId) => {
    const response = await apiClient.patch(`${API_URL}/users/${userId}/activate`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  unlockUserLogin: async (email) => {
    const response = await apiClient.post(`${API_URL}/users/unlock-login`, { email }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  superAdminUnlockLogin: async (email) => {
    const response = await apiClient.post(`${API_URL}/super-admin/unlock-login`, { email }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  impersonateCustomer: async (customerId) => {
    const response = await apiClient.post(`${API_URL}/impersonate/${customerId}`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getCustomerAnalytics: async () => {
    const response = await apiClient.get(`${API_URL}/analytics/customers`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  suspendCustomer: async (customerId) => {
    const response = await apiClient.patch(`${API_URL}/customers/${customerId}/suspend`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  activateCustomer: async (customerId) => {
    const response = await apiClient.patch(`${API_URL}/customers/${customerId}/activate`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Super Admin - All Users
  getAllUsers: async () => {
    const response = await apiClient.get(`${API_URL}/admin/users`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Super Admin - Search Users
  searchUsers: async (query) => {
    const response = await apiClient.get(`${API_URL}/admin/users/search?q=${encodeURIComponent(query)}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Super Admin - Delete User by ID
  adminDeleteUser: async (userId) => {
    const response = await apiClient.delete(`${API_URL}/admin/users/${userId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Super Admin - Delete User by Email
  adminDeleteUserByEmail: async (email) => {
    const response = await apiClient.delete(`${API_URL}/admin/users/by-email/${encodeURIComponent(email)}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Super Admin - Promote to Super Admin
  promoteToSuperAdmin: async (email) => {
    const response = await apiClient.post(`${API_URL}/admin/promote-super-admin`, 
      { email },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  // Super Admin - Assign User to Customer
  assignUserToCustomer: async (email, customerId, role = 'admin') => {
    const response = await apiClient.post(`${API_URL}/admin/assign-to-customer`, 
      { email, customer_id: customerId, role },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  adminResetUserPassword: async (userId, newPassword) => {
    const response = await apiClient.patch(`${API_URL}/users/${userId}/reset-password`, 
      { new_password: newPassword },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  // Articles
  getArticles: async () => {
    const response = await apiClient.get(`${API_URL}/articles`, {
      headers: getAuthHeader()
    });
    return response.data;
  },
  
  getLatestArticle: async () => {
    const response = await apiClient.get(`${API_URL}/articles/latest`, {
      headers: getAuthHeader()
    });
    return response.data;
  },
  
  createArticle: async (data) => {
    const response = await apiClient.post(`${API_URL}/articles`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },
  
  updateArticle: async (id, data) => {
    const response = await apiClient.patch(`${API_URL}/articles/${id}`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },
  
  deleteArticle: async (id) => {
    const response = await apiClient.delete(`${API_URL}/articles/${id}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  uploadArticleImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${API_URL}/upload/article-image`, formData, {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  uploadHeaderImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${API_URL}/upload/header-image`, formData, {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  uploadPwaIcon: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${API_URL}/upload/pwa-icon`, formData, {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  uploadTileIcon: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${API_URL}/upload/tile-icon`, formData, {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Image Library
  getArticleImages: async () => {
    const response = await apiClient.get(`${API_URL}/upload/article-images`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  deleteArticleImage: async (filename) => {
    const response = await apiClient.delete(`${API_URL}/upload/article-images/${filename}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  cloneArticle: async (id) => {
    const response = await apiClient.post(`${API_URL}/articles/${id}/clone`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  moveArticle: async (id, direction) => {
    const response = await apiClient.post(`${API_URL}/articles/${id}/move?direction=${direction}`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getTopArticles: async () => {
    const response = await apiClient.get(`${API_URL}/articles/top`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Push subscriptions
  getVapidPublicKey: async () => {
    const response = await apiClient.get(`${API_URL}/push/vapid-public-key`);
    return response.data;
  },

  subscribeToPush: async (subscription) => {
    const response = await apiClient.post(`${API_URL}/push/subscribe`, subscription, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  subscribeAnonymousPush: async (data) => {
    const response = await apiClient.post(`${API_URL}/push/subscribe-anonymous`, data);
    return response.data;
  },

  getNotificationMode: async (customerId) => {
    const response = await apiClient.get(`${API_URL}/push/notification-mode/${customerId}`);
    return response.data;
  },

  debugPushSubscriptions: async () => {
    const response = await apiClient.get(`${API_URL}/push/debug-subscriptions`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  testPushNotification: async () => {
    const response = await apiClient.post(`${API_URL}/push/test-send`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  testPushToUser: async (userId) => {
    const response = await apiClient.post(`${API_URL}/push/test-send-to-user/${userId}`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  sendScheduledNotifications: async () => {
    const response = await apiClient.post(`${API_URL}/notifications/send-scheduled`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Password reset
  forgotPassword: async (email) => {
    const response = await apiClient.post(`${API_URL}/auth/forgot-password`, { email });
    return response.data;
  },

  // Google SSO
  googleAuthSession: async (sessionId) => {
    const response = await apiClient.post(`${API_URL}/auth/google/session`, {
      session_id: sessionId
    });
    return response.data;
  },

  validateSession: async () => {
    const response = await apiClient.get(`${API_URL}/auth/session/validate`, {
      withCredentials: true
    });
    return response.data;
  },

  resetPassword: async (token, newPassword) => {
    const response = await apiClient.post(`${API_URL}/auth/reset-password`, {
      token,
      new_password: newPassword
    });
    return response.data;
  },

  // Activity logs (Super Admin)
  getActivityLogs: async (params) => {
    const response = await apiClient.get(`${API_URL}/admin/activity-logs`, {
      headers: getAuthHeader(),
      params
    });
    return response.data;
  },

  getActivityStats: async () => {
    const response = await apiClient.get(`${API_URL}/admin/activity-stats`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Wonde Integration
  wondeTestConnection: async () => {
    const response = await apiClient.post(`${API_URL}/wonde/test-connection`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  wondeGetSchools: async () => {
    const response = await apiClient.get(`${API_URL}/wonde/schools`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  wondeSync: async (schoolId) => {
    const response = await apiClient.post(`${API_URL}/wonde/sync`, { school_id: schoolId }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  wondeGetSyncData: async () => {
    const response = await apiClient.get(`${API_URL}/wonde/sync-data`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  wondeCreateUsers: async (userType, wondeIds) => {
    const response = await apiClient.post(`${API_URL}/wonde/create-users`, {
      user_type: userType,
      wonde_ids: wondeIds
    }, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  wondeDeleteSyncData: async () => {
    const response = await apiClient.delete(`${API_URL}/wonde/sync-data`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // ---- Nexus App-Feed (admin) ----
  nexusListArticles: async (params = {}) => {
    const response = await apiClient.get(`${API_URL}/nexus/articles`, {
      headers: getAuthHeader(),
      params
    });
    return response.data;
  },

  nexusCurateArticle: async (articleId, update) => {
    const response = await apiClient.patch(`${API_URL}/nexus/articles/${articleId}/curate`, update, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // ---- Public customer (no auth required) ----
  getPublicCustomer: async (customerId) => {
    const response = await apiClient.get(`${API_URL}/public/customers/${customerId}`);
    return response.data;
  },

  // ---- Nexus App-Feed (public PWA) ----
  getPublicNexusArticles: async (customerId, params = {}) => {
    const response = await apiClient.get(`${API_URL}/public/customers/${customerId}/nexus-articles`, { params });
    return response.data;
  },

  getPublicNexusArticle: async (customerId, articleId) => {
    const response = await apiClient.get(`${API_URL}/public/customers/${customerId}/nexus-articles/${articleId}`);
    return response.data;
  },

  getPublicNexusEvents: async (customerId, params = {}) => {
    const response = await apiClient.get(`${API_URL}/public/customers/${customerId}/nexus-events`, { params });
    return response.data;
  },

  getPublicNexusEventDetail: async (customerId, eventId) => {
    const response = await apiClient.get(`${API_URL}/public/customers/${customerId}/nexus-events/${eventId}`);
    return response.data;
  },

  submitNexusRsvp: async (customerId, eventId, body) => {
    const response = await apiClient.post(
      `${API_URL}/public/customers/${customerId}/nexus-events/${eventId}/rsvp`,
      body
    );
    return response.data;
  }
};

// Helper function to set auth token
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('token', token);
  } else {
    localStorage.removeItem('token');
  }
};

export default api;